import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import Skeleton from '@mui/material/Skeleton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import {
  CheckCircle,
  ListMagnifyingGlass,
  PencilSimpleLine,
  Prohibit,
  ShieldCheck,
  UsersThree
} from '@phosphor-icons/react'
import DataTable from '../../../components/table/DataTable'
import RowActionsMenu from '../../../components/table/RowActionsMenu'
import { RbacStatusChip } from '../../components/RbacChips'
import { rbacTableSkeletonBarSx } from '../../components/constants/rbacTableConstants'

const skeletonBarSx = rbacTableSkeletonBarSx

const SKELETON_COLUMN_WIDTHS = [
  { code: '58%', name: '72%', description: '88%', users: '46%', status: 72 },
  { code: '46%', name: '84%', description: '76%', users: '52%', status: 80 },
  { code: '64%', name: '68%', description: '92%', users: '40%', status: 68 },
  { code: '52%', name: '78%', description: '70%', users: '58%', status: 76 }
]

function roleStatusMeta(role) {
  const normalizedStatus = String(role?.status || '').toLowerCase()

  if (normalizedStatus === 'active' || normalizedStatus === 'activo') {
    return { label: 'ACTIVO', isActive: true }
  }
  if (normalizedStatus === 'inactive' || normalizedStatus === 'inactivo') {
    return { label: 'INACTIVO', isActive: false }
  }

  return {
    label: role?.status ? String(role.status).toUpperCase() : '—',
    isActive: false
  }
}

function formatAssignedUsers(role) {
  const count =
    role.users_count ??
    role.assigned_users_count ??
    role.user_count ??
    role.assigned_users?.length ??
    role.users?.length

  if (count == null) return '—'

  return `${count} ${count === 1 ? 'usuario' : 'usuarios'}`
}

function RolesTableSkeletonRows({ rowCount = 10 }) {
  const count = Math.max(Number(rowCount) || 10, 1)

  return Array.from({ length: count }, (_, index) => {
    const widths = SKELETON_COLUMN_WIDTHS[index % SKELETON_COLUMN_WIDTHS.length]

    return (
      <TableRow
        key={`roles-skeleton-${index}`}
        hover={false}
        sx={{
          cursor: 'default',
          pointerEvents: 'none',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.code} sx={skeletonBarSx} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.name} sx={skeletonBarSx} />
        </TableCell>
        <TableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width={widths.description}
            sx={skeletonBarSx}
          />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.users} sx={skeletonBarSx} />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={24}
            width={widths.status}
            sx={{ ...skeletonBarSx, borderRadius: '12px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton animation="wave" variant="circular" width={28} height={28} sx={{ ...skeletonBarSx, mx: 'auto' }} />
        </TableCell>
      </TableRow>
    )
  })
}

function RolesEmptyState({ title, description }) {
  return { title, description, icon: <ListMagnifyingGlass size={34} weight="regular" /> }
}

export default function RbacRolesTable({
  loading,
  filteredRolesCount,
  paginatedRoles,
  pagination,
  actionMenuAnchorEl,
  actionMenuRole,
  onRoleRowClick,
  onOpenActionMenu,
  onCloseActionMenu,
  onActionMenuExited,
  onEditAction,
  onViewPermissionsAction,
  onViewUsersAction,
  onStatusAction,
  onPageChange,
  onRowsPerPageChange
}) {
  const columns = [
    {
      key: 'code',
      label: 'CODIGO',
      width: '12%',
      truncate: true,
      getTitle: (role) => role.uid || '',
      render: (role) => role.uid || '—'
    },
    {
      key: 'name',
      label: 'NOMBRE DEL ROL',
      width: '22%',
      truncate: true,
      getTitle: (role) => role.name || '',
      render: (role) => role.name || '—'
    },
    {
      key: 'description',
      label: 'DESCRIPCION',
      width: '40%',
      truncate: true,
      getTitle: (role) => role.description || '',
      render: (role) => role.description || '—'
    },
    {
      key: 'users',
      label: 'USUARIOS ASIGNADOS',
      width: '15%',
      render: (role) => formatAssignedUsers(role)
    },
    {
      key: 'status',
      label: 'ESTADO',
      width: '9%',
      align: 'center',
      headerAlign: 'left',
      render: (role) => <RbacStatusChip label={roleStatusMeta(role).label} />
    }
  ]

  const emptyState = RolesEmptyState({
    title: 'No hay roles para mostrar',
    description:
      'No encontramos roles con los filtros actuales. Prueba ajustando la busqueda o restableciendo los filtros.'
  })

  const isInactive = !roleStatusMeta(actionMenuRole).isActive

  const primaryActions = [
    {
      key: 'edit',
      label: 'Editar rol',
      icon: <PencilSimpleLine size={18} weight="regular" />,
      onClick: onEditAction
    },
    {
      key: 'permissions',
      label: 'Ver permisos',
      icon: <ShieldCheck size={18} weight="regular" />,
      onClick: onViewPermissionsAction
    },
    {
      key: 'users',
      label: 'Ver listado de usuarios',
      icon: <UsersThree size={18} weight="regular" />,
      onClick: onViewUsersAction
    }
  ]

  const secondaryActions = [
    {
      key: 'status',
      label: isInactive ? 'Activar rol' : 'Inactivar rol',
      icon: isInactive ? (
        <CheckCircle size={18} weight="regular" />
      ) : (
        <Prohibit size={18} weight="regular" />
      ),
      onClick: onStatusAction
    }
  ]

  return (
    <>
      <DataTable
        ariaLabel="Listado de roles RBAC"
        columns={columns}
        rows={paginatedRoles}
        rowKey="id"
        loading={loading}
        renderSkeletonRows={() => <RolesTableSkeletonRows rowCount={pagination.per_page} />}
        emptyState={emptyState}
        onRowClick={(role) => onRoleRowClick(role.id)}
        pagination={filteredRolesCount > 0 ? pagination : null}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        paginationAriaLabel="Paginacion de roles RBAC"
        paginationSx={{ mb: 0, px: 2.5 }}
        actionsEnabled
        onOpenRowActions={onOpenActionMenu}
      />

      <RowActionsMenu
        anchorEl={actionMenuAnchorEl}
        currentItem={actionMenuRole}
        onClose={onCloseActionMenu}
        onExited={onActionMenuExited}
        primaryActions={primaryActions}
        secondaryActions={secondaryActions}
      />
    </>
  )
}
